import React from "react";
import {BaseComponent, ReactUtility} from "@reapptor-apps/reapptor-react-common";
import {TwoColumns} from "@reapptor-apps/reapptor-react-components";
import CruiseOrder from "@/models/server/CruiseOrder";
import WizardContext from "@/pages/Cruise/WizardContext";
import {CruiseExtra, CruiseGroupSize} from "@/models/Enums";
import Localizer from "@/localization/Localizer";

import styles from "./CruiseSummary.module.scss";

import iconDuration from "./images/duration.png";
import iconDate from "./images/date.png";
import iconAmount from "./images/amount.png";

import iconExtra from "./images/extra.png";
import iconExtraAlcohol from "./images/extra-alcohol.png";
import iconExtraBuffet from "./images/extra-buffet.png";
import iconExtraFishingEquipment from "./images/extra-fishing-equipment.png";
import iconExtraSnacks from "./images/extra-snacks.png";

import iconGroupStandard from "./images/group_standard.png";
import iconGroupBig from "./images/group_big.png";
import iconGroupWaterbus from "./images/group_waterbus.png";
import iconGroupRib from "./images/group_rib.png";
import iconGroupClassic from "./images/group_classic.png";
import iconGroupSail from "./images/group_sail.png";
import iconGroupFishing from "./images/group_fishing.png";
import iconGroupHovercraft from "./images/group_hovercraft.png";
import iconGroupElectric from "./images/group_electric.png";
import routeImage from "./images/route_big.png";
import EnumProvider from "@/providers/EnumProvider";

export interface ICruiseSummaryProps {
    className?: string;
    order: CruiseOrder | WizardContext;
}

export default class CruiseSummary extends BaseComponent<ICruiseSummaryProps> {

    public get model(): CruiseOrder | WizardContext {
        return this.props.order;
    }

    public get groupAmount(): string {
        switch (this.model.groupSize) {
            case CruiseGroupSize.Standard:
                return "1-12";
            case CruiseGroupSize.Big:
                return "13-30";
            case CruiseGroupSize.Waterbus:
                return "30+";
            case CruiseGroupSize.Rib:
                return "1-11";
            case CruiseGroupSize.Classic:
                return "1-11";
            case CruiseGroupSize.Sail:
                return "1-x";
            case CruiseGroupSize.Fishing:
                return "1-x";
            case CruiseGroupSize.Hovercraft:
                return "1-5";
            case CruiseGroupSize.Electric:
                return "1-11";
            default:
                return "?";
        }
    }

    public get groupSizeIcon(): string {
        switch (this.model.groupSize) {
            case CruiseGroupSize.Standard:
                return iconGroupStandard;
            case CruiseGroupSize.Big:
                return iconGroupBig;
            case CruiseGroupSize.Waterbus:
                return iconGroupWaterbus;
            case CruiseGroupSize.Rib:
                return iconGroupRib;
            case CruiseGroupSize.Classic:
                return iconGroupClassic;
            case CruiseGroupSize.Sail:
                return iconGroupSail;
            case CruiseGroupSize.Fishing:
                return iconGroupFishing;
            case CruiseGroupSize.Hovercraft:
                return iconGroupHovercraft;
            case CruiseGroupSize.Electric:
                return iconGroupElectric;
            default:
                return "";
        }
    }

    public get extrasTitle(): string {
        return (this.model.extras && this.model.extras.length > 0)
            ? this.model.extras.map(item => item.format("CruiseExtra")).join(" + ")
            : Localizer.cruiseWizardNoExtras;
    }

    public get amountTitle(): string {
        return (this.model.groupSizeValue)
            ? this.model.groupSizeValue.format("0")
            : Localizer.cruiseWizardPersonAmount.format(this.groupAmount)
    }

    public get estimatedPrice(): string {
        return ((this.model.country) && (this.model.estimatedPrice))
            ? "{0:C} {1}".format(this.model.estimatedPrice, this.model.country.moneySymbol)
            : "";
    }

    public get departFrom(): string {
        return (this.model.departFrom)
            ? this.model.departFrom
            : Localizer.genericNone;
    }

    public get departBy(): string {
        return (this.model.departBy)
            ? "{0:HH:mm}".format(this.model.departBy)
            : Localizer.genericNone;
    }

    public get arriveTo(): string {
        return (this.model.arriveTo)
            ? this.model.arriveTo
            : Localizer.genericNone;
    }

    public get arriveAt(): string {
        return (this.model.arriveAt)
            ? "{0:HH:mm}".format(this.model.arriveAt)
            : Localizer.genericNone;
    }

    public get visitingDuration(): string {
        return (this.model.visitingPointDuration != null)
            ? EnumProvider.getVisitingPointDurationText(this.model.visitingPointDuration)
            : Localizer.genericNone;
    }

    public get isFishing(): boolean {
        return (this.model.groupSize == CruiseGroupSize.Fishing);
    }

    public get isHovercraft(): boolean {
        return (this.model.groupSize == CruiseGroupSize.Hovercraft);
    }

    public get extraIcon(): string {
        if (this.isFishing) {
            return iconExtraFishingEquipment;
        }
        if (this.model.extras?.length == 1) {
            switch (this.model.extras[0]) {
                case CruiseExtra.Alcohol:
                    return iconExtraAlcohol;
                case CruiseExtra.Buffet:
                    return iconExtraBuffet;
                case CruiseExtra.Snacks:
                    return iconExtraSnacks;
            }
        }
        return iconExtra;
    }

    public render(): React.ReactNode {
        return (
            <div id={this.id} className={this.css(styles.cruiseSummary)}>

                <TwoColumns>

                    <div className={styles.routeContainer}>

                        <div className={styles.route}>
                            <img className={this.css(styles.routeImage)} alt={"route"} src={routeImage}/>
                        </div>

                        <div className={styles.routeInfo}>

                            <div>
                                <span>{Localizer.cruiseOrdersDepart}</span>
                                <span>{this.departFrom}</span>
                                <span><img src={iconDuration} alt={"depart at"}/> {this.departBy}</span>
                            </div>

                            <div>
                                <span>{Localizer.cruiseOrdersVisitPoint}</span>
                                <span>{this.model.visitPoint ?? Localizer.genericNone}</span>
                            </div>

                            <div>
                                <span>{Localizer.cruiseOrdersDurationTime}</span>
                                <span>{this.visitingDuration}</span>
                            </div>

                            <div>
                                <span>{Localizer.cruiseOrdersArrive}</span>
                                <span>{this.arriveTo}</span>
                                <span><img src={iconDuration} alt={"arrive at"}/> {this.arriveAt}</span>
                            </div>

                        </div>

                    </div>

                    <div className={"row"}>

                        <div className={"row align-items-center"}>

                            <div className={this.css(styles.item, "col-4")}>

                                <img src={iconDate} alt={"date"}/>

                                <span>{this.model.date.format("D")}</span>

                            </div>

                            {
                                ((this.model.extras) && (this.model.extras.length > 0)) &&
                                (
                                    <div className={this.css(styles.item, "col-4")}>

                                        <img src={this.extraIcon} alt={"extras"}/>

                                        <span>{this.extrasTitle}</span>

                                    </div>
                                )
                            }

                            <div className={this.css(styles.item, "col-4")}>

                                <img src={iconAmount} alt={"amount"}/>

                                <span>{this.amountTitle}</span>

                            </div>

                        </div>

                        <div className={this.css(styles.item, "col-4")}>

                            <img src={this.groupSizeIcon} alt={"group size"}/>

                            <span>{this.model.groupSize?.format("CruiseGroupSize")}</span>

                        </div>

                        <div className={this.css(styles.item, "col-4")}>

                            <img src={iconDuration} alt={"duration"} />

                            <span>{this.model.duration?.format("CruiseDuration")}</span>

                        </div>

                    </div>

                </TwoColumns>

                {
                    (this.estimatedPrice) &&
                    (
                        <div className={"row justify-content-center text-center"}>

                            <h1>{ReactUtility.toMultiLines(Localizer.cruiseWizardEstimatedPrice.format(this.estimatedPrice))}</h1>

                        </div>
                    )
                }

            </div>
        )
    }
}